import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Tabs, Spin, Button, Modal, Select, message, Tooltip } from "antd";
import {
  getProjectById,
  getProjectWorkers,
  getWorkerProgress,
  getUpdateProjectStatus,
  getExpenses,
  getWorkerStats,
} from "../../services/service";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { IMAGE_BASE_URL } from "../../config/api";
import { ActionEye } from "../../assets/icons";
import ProgressCard from "../../components/common/ProgressCard";
import WorkerStatsModal from "./WorkerStatsModal";
import { toast } from "react-toastify";
import "./ProjectDetails.css";

const { TabPane } = Tabs;
const { Option } = Select;
const { confirm } = Modal;

const ProjectDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState(null);
  const [workersData, setWorkersData] = useState([]);
  const [progressData, setProgressData] = useState([]);
  const [expensesData, setExpensesData] = useState([]);
  const [isCompleteModalVisible, setCompleteModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [isWorkerStatsModalOpen, setWorkerStatsModalOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const workerNameById = workersData.reduce((acc, w) => {
    if (w?.id && w?.username) acc[String(w.id)] = w.username;
    return acc;
  }, {});

  const projectId = id || location?.state?.projectId;

  useEffect(() => {
    if (projectId) {
      fetchProjectData(); // This now loads project details, workers (AllocatedUsers), and progress (WorkerProgresses)
      fetchProjectExpenses();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await getProjectById(projectId);
      if (response) {
        console.log('Full API Response:', response?.data); // Debug log

        // Using same structure as manager portal
        const projectDetails = response?.data?.project;
        console.log('Project Details:', projectDetails); // Debug log

        // Add organization from response.data.organization to project details
        if (response?.data?.organization) {
          projectDetails.organization = response.data.organization;
          console.log('Added Organization:', response.data.organization);
        }

        setProjectData(projectDetails);

        // Extract workers from AllocatedUsers array
        if (projectDetails?.AllocatedUsers && projectDetails.AllocatedUsers.length > 0) {
          console.log('Extracted Workers:', projectDetails.AllocatedUsers); // Debug log
          setWorkersData(projectDetails.AllocatedUsers);
        } else {
          console.log('No allocated users found');
          setWorkersData([]);
        }

        // Extract progress from WorkerProgresses array
        if (projectDetails?.WorkerProgresses && projectDetails.WorkerProgresses.length > 0) {
          console.log('Extracted Progress:', projectDetails.WorkerProgresses); // Debug log
          setProgressData(projectDetails.WorkerProgresses);
        } else {
          console.log('No worker progresses found');
          setProgressData([]);
        }
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      message.error("Failed to load project details");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectWorkers = async () => {
    // Workers are now loaded from the main project API response (AllocatedUsers)
    // Keeping this function for refresh purposes
    try {
      const response = await getProjectById(projectId);
      if (response) {
        const projectDetails = response?.data?.project;
        if (projectDetails?.AllocatedUsers && projectDetails.AllocatedUsers.length > 0) {
          setWorkersData(projectDetails.AllocatedUsers);
        } else {
          setWorkersData([]);
        }
      }
    } catch (error) {
      console.error("Error fetching workers:", error);
    }
  };

  const fetchProjectProgress = async () => {
    // Progress is now loaded from the main project API response (WorkerProgresses)
    // This function is kept for backward compatibility but not needed anymore
    try {
      const response = await getProjectById(projectId);
      if (response) {
        const projectDetails = response?.data?.project;
        if (projectDetails?.WorkerProgresses && projectDetails.WorkerProgresses.length > 0) {
          setProgressData(projectDetails.WorkerProgresses);
        } else {
          setProgressData([]);
        }
      }
    } catch (error) {
      console.error("Error fetching progress:", error);
    }
  };

  const fetchProjectExpenses = async () => {
    try {
      // GET expense?project_id=:id
      const response = await getExpenses(1, 100, projectId, null, '', '');
      console.log('Expenses Response:', response); // Debug log
      if (response?.success) {
        setExpensesData(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
    }
  };

  const handleMarkComplete = async () => {
    try {
      const response = await getUpdateProjectStatus({
        project_id: projectId,
        status: false, // false = Completed, true = Active/In Progress
      });
      if (response?.success) {
        message.success("Project marked as completed successfully");
        setCompleteModalVisible(false);
        fetchProjectData();
      }
    } catch (error) {
      console.error("Error marking project complete:", error);
      message.error("Failed to mark project as complete");
    }
  };

  const handleViewWorkerStats = (worker) => {
    setSelectedWorker(worker);
    setWorkerStatsModalOpen(true);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Project not found</p>
      </div>
    );
  }

  const projectInfo = [
    {
      heading: "Project name",
      title: projectData?.title,
    },
    {
      heading: "Client name",
      title: (() => {
        const client = projectData?.Client || projectData?.client;
        return client ? (
          <div className="flex items-center gap-2">
            {client?.image && (
              <img
                src={
                  client.image.startsWith("http")
                    ? client.image
                    : `${IMAGE_BASE_URL}${client.image}`
                }
                alt="Client"
                className="w-8 h-8 rounded-full object-cover"
              />
            )}
            <span className="text-[#4B5563]">
              {client?.username || client?.name || "N/A"}
            </span>
          </div>
        ) : (
          "N/A"
        );
      })(),
    },
    {
      heading: "Organization",
      title: (() => {
        // API returns organization object at root level in response.data
        const orgName = projectData?.organization?.name || projectData?.Organization?.name;
        console.log('Organization:', projectData?.organization); // Debug log
        return orgName || "N/A";
      })(),
    },
    {
      heading: "Manager",
      title: projectData?.Manager?.username || projectData?.manager?.username || "N/A",
    },
    {
      heading: "Start date",
      title: projectData?.expectedStartDate?.slice(0, 10) ?? "N/A",
    },
    {
      heading: "Location",
      title: projectData?.address ?? "N/A",
    },
    {
      heading: "Expense",
      title: projectData?.budget ? `$${projectData.budget}` : "N/A",
    },
    {
      heading: "Estimated completion date",
      title: projectData?.expectedCompletionDate?.slice(0, 10) ?? "N/A",
    },
    {
      heading: "Area",
      title: projectData?.total_area ?? "N/A",
    },
    {
      heading: "Status",
      title: (() => {
        const rawStatus = projectData?.project_status || "Inactive";
        const projectStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
        let colorClass = "bg-gray-100 text-gray-800";
        if (projectStatus === "Active") {
          colorClass = "bg-[#DEE2ED] text-[#1E3A8A]";
        } else if (projectStatus === "Completed") {
          colorClass = "bg-green-100 text-green-800";
        } else if (projectStatus === "Inactive") {
          colorClass = "bg-red-100 text-red-800";
        }
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${colorClass}`}>
            {projectStatus}
          </span>
        );
      })(),
    },
    {
      heading: "Description",
      title: projectData?.description ?? "N/A",
    },
  ];

  return (
    <div className="w-full flex flex-col gap-4 py-6">
      <div className="w-full">
        <ListHeader
          mainHeading="Project Details"
          placeholder="Search"
          btnShow={false}
          linkBreadCrum="/super-admin/all-projects"
          blinkBreadCrumText="All Projects"
          blinkBreadCrumText1="Project Details"
          searchShow={false}
          showLikeFilter={false}
          DropDown={false}
        />
      </div>

      <div className="w-full px-3">
        <div className="bg-white rounded-[8px] shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-[20px] font-semibold text-[#1E3A8A]">
              {projectData?.title}
            </h2>
            {(projectData?.project_status === "Active" || projectData?.project_status === "Inactive") && (
              <button
                onClick={() => setCompleteModalVisible(true)}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-[4px] font-medium text-[14px] transition-colors"
              >
                Mark as Complete
              </button>
            )}
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="project-details-tabs"
          >
            <TabPane tab="View Project" key="1">
              <div className="grid grid-cols-2 gap-4 mt-4">
                {projectInfo.map((item, index) => (
                  <div key={index} className="border-b border-[#E9E9E9] pb-3">
                    <p className="font-semibold text-[14px] text-black mb-1">{item.heading}</p>
                    <div className="text-[#4B5563] text-[14px] font-normal">{item.title}</div>
                  </div>
                ))}
              </div>
            </TabPane>

            <TabPane tab="Project Progress" key="2">
              <div className="mt-4">
                {progressData.length > 0 ? (
                  <div className="space-y-4">
                    {progressData.map((progress, index) => (
                      <ProgressCard
                        key={index}
                        progress={progress}
                        workerNameById={workerNameById}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#B7B7B7] text-[14px]">
                    No progress updates available
                  </div>
                )}
              </div>
            </TabPane>

            <TabPane tab="Workers" key="3">
              <div className="mt-4">
                {workersData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#F8F9FB]">
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Name
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Email
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Phone
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Expertise
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Experience
                          </th>
                          <th className="text-center py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {workersData.map((worker, index) => (
                          <tr
                            key={index}
                            className="hover:bg-[#F8F9FB] transition-colors"
                          >
                            <td className="py-3 px-4 border-b border-[#E9E9E9]">
                              <div className="flex items-center gap-3">
                                {worker?.image && (
                                  <img
                                    src={
                                      worker.image.startsWith('http')
                                        ? worker.image
                                        : `${IMAGE_BASE_URL}${worker.image}`
                                    }
                                    alt={worker?.username}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                )}
                                <span className="font-medium text-[14px] text-[#1E3A8A]">
                                  {worker?.username || 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {worker?.email || 'N/A'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {worker?.phoneNo || 'N/A'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {worker?.expertise || 'N/A'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {worker?.experience ? `${worker.experience} years` : 'N/A'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-center">
                              <Tooltip title="View Worker Stats">
                                <span
                                  onClick={() => handleViewWorkerStats(worker)}
                                  className="cursor-pointer hover:opacity-70 transition-opacity"
                                >
                                  {ActionEye}
                                </span>
                              </Tooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#B7B7B7] text-[14px]">
                    No workers assigned to this project
                  </div>
                )}
              </div>
            </TabPane>

            <TabPane tab="Expense" key="4">
              <div className="mt-4">
                {expensesData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-[#F8F9FB]">
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Description
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Amount
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Tax Amount
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Total Amount
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Vendor
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Invoice Date
                          </th>
                          <th className="text-left py-3 px-4 font-semibold text-[14px] text-black border-b border-[#E9E9E9]">
                            Created By
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {expensesData.map((expense, index) => (
                          <tr
                            key={expense?.id || index}
                            className="hover:bg-[#F8F9FB] transition-colors"
                          >
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {expense?.description || 'N/A'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              ${expense?.input_amount?.toFixed(2) || '0.00'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              ${expense?.tax_amount?.toFixed(2) || '0.00'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563] font-semibold">
                              ${expense?.amount?.toFixed(2) || '0.00'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {expense?.vendor?.title || 'N/A'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {expense?.invoice_date?.slice(0, 10) || 'N/A'}
                            </td>
                            <td className="py-3 px-4 border-b border-[#E9E9E9] text-[14px] text-[#4B5563]">
                              {expense?.ExpenseCreator?.username || 'N/A'}
                              {expense?.ExpenseCreator?.role && (
                                <span className="text-[12px] text-[#B7B7B7] capitalize ml-1">
                                  ({expense.ExpenseCreator.role})
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#B7B7B7] text-[14px]">
                    No expenses found for this project
                  </div>
                )}
              </div>
            </TabPane>

          </Tabs>
        </div>
      </div>

      {/* Mark Complete Confirmation Modal */}
      <Modal
        title="Mark Project as Complete"
        open={isCompleteModalVisible}
        onOk={handleMarkComplete}
        onCancel={() => setCompleteModalVisible(false)}
        okText="Yes, Mark Complete"
        cancelText="Cancel"
        okButtonProps={{ className: "modal-orange-btn" }}
      >
        <p>Are you sure you want to mark this project as completed?</p>
        <p className="text-[#4B5563] text-[14px] mt-2">
          The project status will be changed from Active to Completed.
        </p>
      </Modal>

      {/* Worker Stats Modal */}
      <WorkerStatsModal
        visible={isWorkerStatsModalOpen}
        onClose={() => {
          setWorkerStatsModalOpen(false);
          setSelectedWorker(null);
        }}
        worker={selectedWorker}
      />
    </div>
  );
};

export default ProjectDetails;
